import { DownOutlined } from '@ant-design/icons';
import { useLocalStorageState, useSetState } from 'ahooks';
import { Card, Radio, Select, Tabs, Tooltip } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio/interface';
import classnames from 'classnames';
import { flattenDeep } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import DownloadLine from '@/assets/images/icons/download-line.react.svg?react';
import { exportCSV } from '@/infrastructure/export-csv';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { NumberType } from '@/presentation/components/form/components/select-number';
import styles from '@/presentation/components/issue-trend-analysis/index.module.scss';
import { Chart, RATE } from '@/presentation/components/issue-trend-analysis/line-chart';
import { GetDashboardData, MetricName } from '@/presentation/components/trend/api';
import { ISSUE_TREND_ANALYSIS_VISIBLE } from '@/presentation/config/storage';
import { TrendChart } from '@/presentation/pages/traffic/analytics/trend';
import { IssueType } from '@/proto/issue';
import { IGetDashboardDataRsp, MetricSubType, MetricType, TimeType } from '@/proto/metric';
import { fromPBTime } from '@raft/utils';

interface IProps {
  issueType: IssueType;
  metricType: MetricType;
  token: string;
  headerToken: string;
  pgId: string;
  timeWindow?: TimeType;
  startDateStr?: string;
  endDateStr?: string;
  launchDuration?: NumberType
}

const { TabPane } = Tabs;

enum UnitEnum {
  Hundred=0,
  Thousand=1,
  TenThousand=2
}

const options = [
  {
    label: '百分比',
    value: UnitEnum.Hundred,
  },
  {
    label: '千分比',
    value: UnitEnum.Thousand,
  },
  {
    label: '万分比',
    value: UnitEnum.TenThousand,
  },
];

export const RATE_LIST = [
  MetricSubType.SUB_TYPE_COUNT,
  MetricSubType.SUB_TYPE_DEVICE_RATIO,
  MetricSubType.SUB_TYPE_COUNT_RATIO,
  MetricSubType.SUB_TYPE_USER_RATIO,
];
export const COMBINE_DISPLAY_LIST = [
  MetricSubType.SUB_TYPE_COUNT,
];

export const NetTrendAnalysis: FC<IProps> = (props: IProps) => {
  const {
    metricType, token, timeWindow,
    headerToken, pgId,
  } = props;
  const displayGroup = [
    {
      tabName: '异常次数',
      metricName: MetricName.Count,
      type: 'bar',
    },
    {
      tabName: '异常率',
      metricName: MetricName.CountRatio,
      unit: '%',
      type: 'line',
    },
  ];
  const [storageVisible, setStorageVisible] = useLocalStorageState<boolean>(
    `${ISSUE_TREND_ANALYSIS_VISIBLE}-${pgId}`,
    {
      defaultValue: true,
    },
  );
  const [chartData, setChartData] = useState<IGetDashboardDataRsp>({});
  const [state, setState] = useSetState({
    unit: UnitEnum.Hundred,
    statisticalDimension: false,
    tabKey: MetricSubType.SUB_TYPE_COUNT,
    loading: false,
    tabName: '异常次数/异常率',
    displayGroups: displayGroup,
  });
  const unitChange = (value: number) => {
    setState({ unit: value });
  };

  const statisticalDimensionChange = (e: RadioChangeEvent) => {
    setState({ statisticalDimension: e.target.value });
    getChartData(token, e.target.value);
  };

  useEffect(() => {
    if (token && headerToken) {
      getChartData(token, state.statisticalDimension);
    }
  }, [headerToken]);

  // 获取趋势分析数据
  const getChartData = async (token: string, statisticalDimension: boolean) => {
    setState({ loading: true });
    const totalize = timeWindow && [TimeType.MINUTE5, TimeType.HOUR].includes(timeWindow)
      ? statisticalDimension : false;
    const params = { metricType, totalize, subTypes: [MetricSubType.SUB_TYPE_TRAFFIC_EXCEPTION], token };
    const data = await GetDashboardData(params);
    setState({ loading: false });
    setChartData(data);
  };


  const getTabName = (value: number): string => {
    switch (value) {
      case MetricSubType.SUB_TYPE_DEVICE_RATIO:
        return '影响设备率';
      case MetricSubType.SUB_TYPE_COUNT_RATIO:
        return '异常率';
      case MetricSubType.SUB_TYPE_USER_RATIO:
        return '影响用户率';
      case MetricSubType.SUB_TYPE_COUNT:
        return '异常次数';
      case MetricSubType.SUB_TYPE_DEVICE_UV:
        return '影响设备数';
      case MetricSubType.SUB_TYPE_USER_UV:
        return '影响用户数';
      case MetricSubType.SUB_TYPE_TRAFFIC_EXCEPTION:
        return '异常流量';
      default:
        return '';
    }
  };

  const tabChange = (value: string) => {
    const newValue = Number(value);
    setState({ tabKey: newValue, tabName: getTabName(newValue) });
    if (COMBINE_DISPLAY_LIST.includes(newValue)) {
      const { displayGroups } = tabList.find(tab => tab.key === newValue)!;
      setState({ tabKey: newValue, tabName: getTabName(newValue), displayGroups: displayGroups! });
    }
  };


  const tabList = [
    { tab: '异常次数/异常率', name: '异常次数/异常率', key: MetricSubType.SUB_TYPE_COUNT, displayGroups: displayGroup },
    { tab: '影响用户数', name: '影响用户数', key: MetricSubType.SUB_TYPE_USER_UV },
    { tab: '影响用户率', name: '影响用户率', key: MetricSubType.SUB_TYPE_USER_RATIO, unit: '%' },
    { tab: '影响设备数', name: '影响设备数', key: MetricSubType.SUB_TYPE_DEVICE_UV },
    { tab: '影响设备率', name: '影响设备率', key: MetricSubType.SUB_TYPE_DEVICE_RATIO, unit: '%' },
    { tab: '异常流量', name: '异常流量', key: MetricSubType.SUB_TYPE_TRAFFIC_EXCEPTION, unit: 'MB' },
  ];
  // 导出数据
  const exportData = async () => {
    const timePoints = (chartData?.timePoints || []).map(timePoint => fromPBTime(timePoint).format('YYYY-MM-DD HH:mm:ss')) || [];
    const head = [['时间范围', ...flattenDeep(tabList.map((item) => {
      if (item.displayGroups) {
        return item.displayGroups.map(group => `${group.tabName}${group.unit ? `（${group.unit}）` : ''}`);
      };
      return `${item.name}${item.unit ? `（${item.unit}）` : ''}`;
    }))]];
    const body = [];
    const deviceRatio = chartData?.data?.find(item => item.metricName === MetricName.DeviceRatio)?.dataPoints;
    const avgDeviceUvRatio = chartData?.data?.find(item => item.metricName === MetricName.avgDeviceUvRatio)?.dataPoints;
    const countRatio = chartData?.data?.find(item => item.metricName === MetricName.CountRatio)?.dataPoints;
    const userUvRatio = chartData?.data?.find(item => item.metricName === MetricName.UserUvRatio)?.dataPoints;
    const count = chartData?.data?.find(item => item.metricName === MetricName.Count)?.dataPoints;
    const launchCount = chartData?.data?.find(item => item.metricName === MetricName.DauLaunchTimes)?.dataPoints;
    const deviceUv = chartData?.data?.find(item => item.metricName === MetricName.DeviceUv)?.dataPoints;
    const userUv = chartData?.data?.find(item => item.metricName === MetricName.UserUv)?.dataPoints;
    const dauDeviceUv = chartData?.data?.find(item => item.metricName === MetricName.DauDeviceUv)?.dataPoints;
    for (let i = 0; i < timePoints.length; i++) {
      body.push([
        timePoints[i],
        (count?.[i] || ''),
        (countRatio?.[i] ? ((countRatio?.[i] * RATE[0])).toFixed(2) : ''),
        (userUv?.[i] || ''),
        (userUvRatio?.[i] ? ((userUvRatio?.[i] * RATE[0])).toFixed(2) : ''),
        (deviceUv?.[i] || ''),
        (deviceRatio?.[i] ? ((deviceRatio?.[i] * RATE[0])).toFixed(2) : ''),
        (avgDeviceUvRatio?.[i] ? ((avgDeviceUvRatio?.[i] * RATE[0])).toFixed(2) : ''),
        (launchCount?.[i] || ''),
        (dauDeviceUv?.[i] || ''),
      ]);
    }
    await exportCSV('流量-异常分析-趋势分析', head, body);
  };

  const renderChart = () => {
    if (state.tabKey === MetricSubType.SUB_TYPE_TRAFFIC_EXCEPTION || state.tabKey === MetricSubType.SUB_TYPE_COUNT) {
      return <TrendChart
        timeWindow={timeWindow || 0}
        token={token}
        fields={{}}
        metricSubType={state.tabKey}
        metricType={MetricType.TYPE_TRAFFIC_RESOURCE}
        showTools={false}
        showCount={false}
        chartData={chartData}
        displayGroups={state.tabKey === MetricSubType.SUB_TYPE_COUNT ? state.displayGroups : undefined}
        unit={state.tabKey === MetricSubType.SUB_TYPE_COUNT ? state.unit : undefined}
      />;
    }

    return <Chart
      tabName={state.tabName}
      loading={state.loading}
      chartData={chartData}
      unit={state.unit}
      timeWindow={timeWindow}
      tabKey={state.tabKey}
    />;
  };

  return <Card className={styles.card}>
    <div className={styles.head}>
      <span className={styles.headTitle}>趋势分析</span>
      <div>
        {chartData?.data?.length
          ? <span className={styles.downloadIcon} onClick={exportData}><Tooltip
            title={'导出趋势数据'}><DownloadLine/></Tooltip></span> : null}
        <span className={styles.switch} onClick={() => {
          setStorageVisible(visible => !visible);
          reportClick(
            pgId,
            !storageVisible ? CUSTOM_ACTIONS.commonExpandTrendAnalysis : CUSTOM_ACTIONS.commonCloseTrendAnalysis,
            {
              from: pgId,
            },
          );
        }}>
          <span>{storageVisible ? '收起' : '展开'}</span>
          <DownOutlined className={classnames({
            [styles.headIcon]: true,
            [styles.up]: storageVisible,
            [styles.down]: !storageVisible,
          })}/>
        </span>
      </div>

    </div>
    {storageVisible ? <div className={styles.center}>

      <div className={styles.filters}>
        {RATE_LIST.includes(state.tabKey)
          ? <Select onChange={unitChange} value={state.unit} className={styles.unit} options={options}/> : null}
        {timeWindow && [TimeType.MINUTE5, TimeType.HOUR].includes(timeWindow)
          ? <div className={styles.statisticalDimension}>
            <label className={styles.label}>统计维度</label>
            <Radio.Group onChange={statisticalDimensionChange} value={state.statisticalDimension}>
              <Radio value={false}>{timeWindow === TimeType.MINUTE5 ? '5分钟' : '1小时'}</Radio>
              {state.tabKey !== MetricSubType.SUB_TYPE_TRAFFIC_EXCEPTION && <Radio value={true}>累计</Radio>}
            </Radio.Group>
          </div> : null}
      </div>
      <Tabs onChange={tabChange} activeKey={String(state.tabKey)} className={styles.tab}>
        {tabList.map(item => <TabPane tab={item.tab} key={item.key}/>)}
      </Tabs>
    </div> : null}
    {storageVisible ? renderChart() : null}

  </Card>;
};

